import { Policy } from '../page';

interface PolicyDetailModalProps {
  policy: Policy;
  onClose: () => void;
  onEdit: (policy: Policy) => void;
}

export default function PolicyDetailModal({ policy, onClose, onEdit }: PolicyDetailModalProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-700';
      case 'Draft': return 'bg-yellow-100 text-yellow-700';
      case 'Archived': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'HR': return 'ri-team-line';
      case 'Safety': return 'ri-shield-check-line';
      case 'Operations': return 'ri-settings-3-line';
      case 'Finance': return 'ri-money-dollar-circle-line';
      case 'IT': return 'ri-computer-line';
      case 'Legal': return 'ri-scales-3-line';
      case 'Quality': return 'ri-award-line';
      case 'Environmental': return 'ri-leaf-line';
      default: return 'ri-file-text-line';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-8">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0">
                <i className={`${getCategoryIcon(policy.category)} text-3xl text-white w-8 h-8 flex items-center justify-center`}></i>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold">{policy.title}</h2>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(policy.status)}`}>
                    {policy.status}
                  </span>
                </div>
                <p className="text-teal-50 text-sm">{policy.description}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="ml-4 w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/20 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl w-6 h-6 flex items-center justify-center"></i>
            </button>
          </div>

          {/* Meta Information */}
          <div className="flex flex-wrap gap-6 text-sm text-teal-50">
            <div className="flex items-center gap-2">
              <i className="ri-folder-line w-4 h-4 flex items-center justify-center"></i>
              <span>{policy.category}</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
              <span>Effective: {new Date(policy.effectiveDate).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-git-branch-line w-4 h-4 flex items-center justify-center"></i>
              <span>Version {policy.version}</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
              <span>Updated by {policy.updatedBy}</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
              <span>Last updated: {new Date(policy.lastUpdated).toLocaleDateString()}</span>
            </div>
          </div>

          {/* Tags */}
          {policy.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {policy.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white rounded-full text-xs font-medium"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8">
          <div className="prose prose-sm max-w-none">
            <div className="text-gray-700 whitespace-pre-wrap leading-relaxed text-base">
              {policy.content}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium cursor-pointer whitespace-nowrap"
            >
              Close
            </button>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  onEdit(policy);
                  onClose();
                }}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-edit-line w-4 h-4 flex items-center justify-center"></i>
                Edit Policy
              </button>
              <button
                onClick={() => window.print()}
                className="px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-printer-line w-4 h-4 flex items-center justify-center"></i>
                Print
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
